import {PropPath, PropPathPart} from '@mintdata/mint-kit/api/properties/PropPath';
import {Type} from '@mintdata/mint-kit/api/types/Type';

export const DRAFT_STATE = Symbol.for('draft-state');
export const IS_DRAFT = Symbol.for('is-draft-state');

export function isDraft(value:any)
{
    return !!value && !!value[IS_DRAFT];
}

export function isProxy(value:any)
{
    return !!value && !!value[DRAFT_STATE];
}

export function isDraftable(value:any)
{
    if( !value || typeof value !== 'object' )
        return false;

    return !Type.isInstanceOfIValue(value);
}

export function has(thing:any, prop:PropertyKey)
{
    return Object.prototype.hasOwnProperty.call(thing, prop);
}

export type Change = {
    path:PropPath,
    newValue:any
}

export type Scope = {
    drafts:Array<Draft>;
    changes:Array<Change>
}

export type Draft = {
    // Modification scope.
    scope:Scope,

    // The parent draft state.
    parent:Draft,

    path:PropPath,
    // The base object.
    base:any,
    // The base proxy.
    proxy?:any,

    // Sub Draft.
    drafts:{[key:string]:Draft},

    // Method to revoke this proxy.
    revoke?:any,
}

const objectTraps:any = {
    get,
    set,
    getOwnPropertyDescriptor,
    has(draft:Draft, prop:PropertyKey)
    {
        return has(draft.drafts, prop) || has(draft.base, prop);
    },
    ownKeys(draft:Draft)
    {
        return Reflect.ownKeys(draft.base);
    },
    deleteProperty()
    {
        throw new Error('Object.deleteProperty() cannot be used on a draft');
    },
    defineProperty()
    {
        throw new Error('Object.defineProperty() cannot be used on a draft');
    },
    getPrototypeOf(draft:Draft)
    {
        return Object.getPrototypeOf(draft.base);
    },
    setPrototypeOf()
    {
        throw new Error('Object.setPrototypeOf() cannot be used on a draft');
    }
};

const arrayTraps:any = {};
Object.keys(objectTraps).forEach(key =>
{
    const fn = objectTraps[key];
    arrayTraps[key] = function()
    {
        arguments[0] = arguments[0][0];
        return (fn as any).apply(this, arguments);
    };
});
arrayTraps.set = function(draft:Draft, prop:PropertyKey, value:any)
{
    if( prop !== 'length' && isNaN(parseInt(prop as string)) )
    {
        throw new Error('Only supports setting array indices and the \'length\' property');
    }
    return objectTraps.set.call(this, draft[0], prop, value);
};

// returns the object we should be reading the current value from, which is base, until some change has been made
function get(draft:Draft, prop:PropertyKey)
{
    // special prop to determine existing proxies
    if( prop === DRAFT_STATE ) return draft;

    let {drafts} = draft;

    if( has(drafts, prop) )
    {
        const sub = drafts[prop as any];
        return isDraft(sub) ? sub.proxy : sub;
    }

    const value = draft.base[prop];
    if( !isDraftable(value) )
        return value;

    const subDraft = drafts[prop as any] = createDraft(value, draft, prop, draft.scope);

    return subDraft.proxy;
}

function set(draft:Draft, prop:PropertyKey, value:any)
{
    // check if existing proxy is assigned
    let newValue = value;
    let subDraft = value;
    if( isProxy(value) )
    {
        subDraft = value[DRAFT_STATE];
        newValue = subDraft.base;
    }
    else if( isDraftable(value) )
    {
        subDraft = createDraft(value, draft, prop, draft.scope);
        newValue = value;
    }

    draft.drafts[prop as any] = subDraft;

    draft.scope.changes.push({
        path: draft.path.plus(prop as PropPathPart),
        newValue: newValue
    });

    return true;
}

function getOwnPropertyDescriptor(draft:Draft, prop:PropertyKey)
{
    const owner = draft.base;
    const desc = Reflect.getOwnPropertyDescriptor(owner, prop);
    if( desc )
    {
        desc.writable = true;
        desc.configurable = !Array.isArray(owner) || prop !== 'length';
    }
    return desc;
}

export function createDraft(base:any, parent:Draft, parentProp:PropertyKey, scope:Scope)
{
    const draft:Draft = {
        scope,
        parent,
        path: parent ? parent.path.plus(parentProp as PropPathPart) : PropPath.ROOT,
        base,
        drafts: {}
    };
    draft[IS_DRAFT] = true;

    // `[draft]` is used for arrays, to make sure the proxy is array-ish and not violate invariants (@from `immer` sources)
    const {revoke, proxy} = Array.isArray(base)
        ? Proxy.revocable([draft], arrayTraps)
        : Proxy.revocable(draft, objectTraps);

    draft.proxy = proxy;
    draft.revoke = revoke;

    scope.drafts.push(draft);

    return draft;
}

export function trackChangesProxy<T>(target:T)
{
    const scope:Scope = {
        drafts: [],
        changes: []
    };

    const draft = createDraft(target, null, null, scope);

    return {
        proxy: () => draft.proxy as T,
        changes: () => scope.changes,
        revoke: () => scope.drafts.forEach(draft => draft.revoke())
    };
}
